/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment';
/* eslint-disable no-inner-declarations */
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchFormProps } from './SearchForm.types';
import { StyledSearchForm, StyledSearchFormWrapper } from './SearchForm.styles';
import { SearchFormState, useSearchFormStore } from '../../modules/store/searchFormStore';
import {
  getCurrentDateObject,
  getDateObj,
  getNextDateObjectRange,
  getTotalNumberOfDays,
} from '@marriott/mi-ui-library';
import SearchFormView from './SearchForm.controller';
import clsx from 'clsx';
import { StaticDataContextProvider } from '../../modules/context';
import { DATE_FORMAT_VALUE, DATES, HOME_PAGE_HEADER_CONST_HEIGHT } from '../../modules/constants/Calendar.constant';
import { DESTINATION, RESORTS_HIDDEN_FIELDS, ROOMS_AND_GUESTS } from '../../modules/store/store.constants';
/* eslint-disable @nx/enforce-module-boundaries */
import { getSessionData } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore, usePersistentGlobalStore } from '@marriott/mi-store-utils';
/* eslint-enable @nx/enforce-module-boundaries */
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { toRem } from '@marriott/mi-ui-library';
import { constructSearchformLabels } from '../../modules/utils/searchFormHelper';
import { getRoomRange } from '../../modules/utils/helpers';
import React from 'react';
import { DATE_FORMAT_ISO } from '../../modules/constants/lib/constants';

export const getDateFormattedFromURL = (date?: string): string => {
  return moment(date, DATE_FORMAT_VALUE)?.format(DATE_FORMAT_ISO);
};

/**
 *model ={<An AEM model with all author value>}
 * @param props
 * @returns
 */
// Use named rather than default exports.
export const SearchForm: FC<SearchFormProps> = React.memo(props => {
  const searchFormOnHomepage = props?.model?.['enableOverlayTop']; // this is the identifier which tells if the searchform is on HP or any other page
  const {
    model,
    isPositionedExternally,
    isSticky,
    refCallback,
    isNonStickyVariant,
    isExternal,
    isSearchFormUsedInBrandsPage,
    lengthOfStayDefaultOverride,
    disableDropShadow,
    brandSlug,
  } = props;
  const TabbedForm =
    typeof model?.['variation'] === 'string' && model?.['variation'].toLowerCase() === 'phoenix' ? true : false; // this tells us if the searchform is Home Page Search form or Advanced Search form

  const { envVarsObject } = useClientEnvVarsStore();
  const requestId = envVarsObject?.['REQUEST_ID'];

  const [isStickySearchForm, setIsStickySearchForm] = useState(isPositionedExternally ? isSticky : false);
  /**
   * constructSearchformLabels function maps few labels to work with common react code ,
   * so we do not have to maintain 2 different labels for single component even if AEM
   * is sending two different label names for Advanced search form and Home page search form.
   */
  constructSearchformLabels(model);
  const callGetSessionData = props?.model?.numberOfSearchForms || props?.model?.enableOverlayTop ? true : false; // flag to determine if get session call is required.
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData); // function to update global data store
  const globalData = usePersistentGlobalStore((state: any) => state.globalData); // global data extracted from global data store
  /**
   * shop store to access the data
   */
  const dates = useSearchFormStore((state: SearchFormState) => state.dates);
  const shopStoreInitialization = useSearchFormStore((state: any) => state.shopStoreInitialization);
  const setSearchFormState = useSearchFormStore((state: any) => state.setSearchFormState); // function to update search form data in store
  const isBaseLineSearchTabbedView = TabbedForm; // variable to determine whether tabbed view is in Base line search form or from the TabbedSearchForm
  const [currentLocale, setCurrentLocale] = useState(props.currentLocale);
  const [isClientSideRenderedSearchform, setIsClientSideRenderedSearchform] = useState(false);

  /**
   * import search from component with modification
   * and state implemention
   */

  useEffect(() => {
    setSearchFormState(
      [RESORTS_HIDDEN_FIELDS],
      {
        [RESORTS_HIDDEN_FIELDS]: {
          hotelType: model?.hotelType,
          activities: model?.resortActivities,
          amenities: model?.amenities,
        },
      },
      true
    );
  }, [model?.hotelType, model?.resortActivities, model?.amenities, setSearchFormState]);

  // This effect initializes the search date range when a valid
  // lengthOfStayDefaultOverride value is provided (> 0).
  // It sets:
  //   - fromDate to the current date
  //   - toDate based on the default length of stay
  //   - numberOfNights and lengthOfStay to the override value
  // Runs only when lengthOfStayDefaultOverride changes

  useEffect(() => {
    // Run only if the override is valid (present and > 0)
    if (
      !lengthOfStayDefaultOverride ||
      typeof lengthOfStayDefaultOverride !== 'number' ||
      lengthOfStayDefaultOverride <= 0 ||
      lengthOfStayDefaultOverride > Number(model?.maxNoOfFlexibleNights)
    ) {
      return;
    }

    const currentDate = getCurrentDateObject();
    const nextDate = getNextDateObjectRange(currentDate, lengthOfStayDefaultOverride);

    setSearchFormState([DATES], {
      ...dates,
      [DATES]: {
        ...dates,
        fromDate: currentDate,
        toDate: nextDate,
        numberOfNights: lengthOfStayDefaultOverride,
        lengthOfStay: lengthOfStayDefaultOverride,
      },
    });
  }, [lengthOfStayDefaultOverride]);

  useEffect(() => {
    // Initialize the zustand store with the sessionData available in props
    if (!callGetSessionData && !model?.isTabbedSearchForm) {
      shopStoreInitialization(globalData?.sessionData, model);
    }
  }, [globalData]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.document) {
      setIsClientSideRenderedSearchform(true);
    }

    setFormLoaded(true); // setting this state as true , as search form is loaded on client side for Advanced search page
    async function getSession() {
      // Extract the sessionID cookie value from browser cookies
      const sessionID = document?.cookie?.split('sessionID=')[1]?.split(';')[0];
      // Make the client side GET session call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await getSessionData(sessionID, false, false, requestId);
      // Initialize the zustand store with the new sessionData
      shopStoreInitialization(response?.sessionData, model);
      // Update the sessionData value in global-store
      updateGlobalData('sessionData', response?.sessionData);
    }
    // make the client side session call from SearchForm component
    if (callGetSessionData) {
      getSession();
    }
    /**
     * to set the homepage Header height
     */
    //since this element is from AEM script, need to handle it with query selector.
    const htlHeaderComp = document.querySelector('.m-header');
    const homePageBannerComp = document.querySelector('.ab');
    if (!homePageBannerComp) return;
    const resizeObserver = new ResizeObserver(() => {
      const htlHeaderHeight = htlHeaderComp?.getBoundingClientRect().height ?? HOME_PAGE_HEADER_CONST_HEIGHT;
      homePageBannerCompHeight.current = homePageBannerComp ? homePageBannerComp?.clientHeight : 0;
      setTopPosition(htlHeaderHeight + homePageBannerCompHeight.current);
    });
    resizeObserver.observe(homePageBannerComp);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  // For book page. Because the 800error page of book uses the same component, when the currentLocale is undefined, retrieve the locale from sessionData
  useEffect(() => {
    const sessionData = !!(typeof window !== 'undefined' && window.document) && window['sessionData'];
    currentLocale ?? setCurrentLocale(sessionData?.locale);
  }, []);

  useEffect(() => {
    if (isPositionedExternally) {
      if (isSticky) {
        setIsStickySearchForm(true);
      } else {
        setIsStickySearchForm(false);
      }
    }
  }, [isSticky, isPositionedExternally]);

  const scrollPosition = useRef<number>(0);
  const searchFormContainerElRef = useRef<HTMLDivElement>(null);
  const searchFormContainerRef = useRef<HTMLDivElement>(null);

  // Convert to ref to fix React Compiler error - doesn't need to trigger re-renders
  const lastScrollPosition = useRef<number>(0);

  // Convert to ref to fix React Compiler error - doesn't need to trigger re-renders
  const scrollThreshold = useRef<number>(0);
  const [searchformScrollThreshold, setSearchformScrollThreshold] = useState(0);
  const [homePageHeaderHeight, setHomePageHeaderHeight] = useState(0);
  // Convert to ref to fix React Compiler error - doesn't need to trigger re-renders
  const homePageBannerCompHeight = useRef<number>(0);
  const searchFormPadding = 24;
  const [topPosition, setTopPosition] = useState(0);

  const searchFormPositionWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPositionedExternally) {
      if (refCallback && searchFormContainerRef.current) {
        refCallback(
          searchFormContainerRef.current,
          // this is hacky and should be removed ASAP,
          // but we need to account for the form's initial top-padding
          searchFormContainerRef.current?.getBoundingClientRect().top -
            (searchFormContainerElRef?.current?.getBoundingClientRect().top || 0)
        );
      }
      return;
    }

    /**
     * to set the homepage Header height
     */
    //since this element is from AEM script, need to handle it with query selector.
    const htlHeaderComp = document.querySelector('.m-header');
    const homePageBannerComp = document.querySelector('.ab');
    const htlHeaderHeight = htlHeaderComp?.getBoundingClientRect().height ?? HOME_PAGE_HEADER_CONST_HEIGHT;
    homePageBannerCompHeight.current = homePageBannerComp ? homePageBannerComp?.clientHeight : 0;

    const inititalTopPosition = htlHeaderHeight + homePageBannerCompHeight.current;
    setTopPosition(inititalTopPosition);

    // set initial top position for search form, references htl fragment header
    setSearchformScrollThreshold(htlHeaderHeight + homePageBannerCompHeight.current + searchFormPadding);
    setHomePageHeaderHeight(htlHeaderHeight);
  }, []);

  const setSearchFormTopPosition = () => {
    const offsetVal = isBaseLineSearchTabbedView ? 144 : 24;
    const formEl = searchFormPositionWrapperRef?.current;
    let searchFormTopPositionValue = formEl?.getBoundingClientRect()?.top;
    if (searchFormTopPositionValue && searchFormTopPositionValue < offsetVal) {
      searchFormTopPositionValue = 0;
    }
    if (typeof searchFormTopPositionValue === 'number') {
      let value: number;

      if (isBaseLineSearchTabbedView) {
        value = searchFormTopPositionValue - offsetVal;
      } else if (isSearchFormUsedInBrandsPage) {
        // !important: remove when Brands team moves to phoenix-header
        const header = document.querySelector('.m-header') as HTMLElement | null;
        const headerHeight = header ? Math.round(header.getBoundingClientRect().height || 0) : 0;
        value = searchFormTopPositionValue - headerHeight * 2;
      } else {
        value = searchFormTopPositionValue;
      }

      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: { value },
      });
    } else {
      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: {
          value: window.scrollY,
        },
      });
    }
  };

  /**
   * Below use effect attaches an event for scroll event and is written in such a way that it
   * only triggers the auto scroll functionality.
   *  What is Auto Scroll functionality ? --> When user open DatePicker or any other component(dropdown modal),
   * for some users the screen size is smaller to accomodate the complete modal in the viewport with respect to search form position,
   * hence we auto scroll the searchform to the top so that the dropdown modal will is completely visible to the user.
   */
  useEffect(() => {
    if (!model?.isTabbedSearchForm || isBaseLineSearchTabbedView) {
      setSearchFormTopPosition();
    }
  }, [model?.isTabbedSearchForm, isBaseLineSearchTabbedView, isClientSideRenderedSearchform]);

  /**
   * checks the scroll position wrt threshold(header) so as to add the necessary scroll CSS
   */
  const handleScrollPosition = (updatedCurrentScrollPosition: number, sT: number) => {
    if (updatedCurrentScrollPosition > sT && scrollPosition.current < sT) {
      scrollPosition.current = updatedCurrentScrollPosition;

      /**
       *makes the SearchForm full width if user scrolls and vice versa if back to original state
       */

      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.add('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.remove('search-form-wrapper');
      }
    } else if (updatedCurrentScrollPosition <= sT && scrollPosition.current > sT) {
      scrollPosition.current = updatedCurrentScrollPosition;
      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.remove('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.add('search-form-wrapper');
      }
    }
  };

  /**
   * function to handle the scroll state of the searchForm when user scrolled(fsd-80422)
   */
  const handleScroll = () => {
    const currentScrollPosition = window.scrollY;
    scrollThreshold.current = searchformScrollThreshold;

    //since this element is from AEM header or alert banner, need to handle it with query selector.
    const alertBanner = document.querySelector('.ab__desc');
    const alertBannerComp = document.querySelector('.ab');
    const alertBannerHeight = alertBannerComp?.clientHeight ?? 0;
    const htlHeaderComp = document.querySelector('.m-header');
    const htlHeaderTop = htlHeaderComp?.getBoundingClientRect().top;

    let updatedCurrentScrollPosition = currentScrollPosition;
    if (
      currentScrollPosition > lastScrollPosition.current &&
      currentScrollPosition >= homePageHeaderHeight &&
      htlHeaderTop
    ) {
      updatedCurrentScrollPosition += Math.abs(htlHeaderTop);
      scrollThreshold.current += Math.abs(htlHeaderTop);
      if (updatedCurrentScrollPosition < scrollThreshold.current) {
        const calculatedTop = homePageHeaderHeight + alertBannerHeight;
        setTopPosition(calculatedTop);
      }
    }
    /**
     * Close the alert Banner on the scroll of the page
     */
    if (updatedCurrentScrollPosition > alertBannerHeight) {
      alertBanner && alertBanner?.classList.add('hide-visibility');
      alertBannerComp && alertBannerComp?.classList.add('hide-alert');
    }
    /**
     * checks the scroll previous position and current position
     */
    /**
     * remove the sticky css class if searchForm back to top
     */
    if (updatedCurrentScrollPosition === 0) {
      scrollPosition.current = 0;
      setIsStickySearchForm(false);
      searchFormContainerElRef.current?.classList.remove('sticky-search-top');
      searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      searchFormContainerElRef.current?.classList.remove('search-container-top');
      const topPosition = homePageHeaderHeight + alertBannerHeight;

      //when updatedCurrentScrollPosition === 0 , searchform should have 'search-form-wrapper' class applied.
      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.remove('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.add('search-form-wrapper');
      }
      setTopPosition(topPosition);
    } else if (updatedCurrentScrollPosition > scrollThreshold.current) {
      // When scroll position is greater than the top offset of the searchform
      /**
       * When scrolling down add the sticky search container class
       */
      if (updatedCurrentScrollPosition > lastScrollPosition.current) {
        setIsStickySearchForm(true);
        handleScrollPosition(updatedCurrentScrollPosition, scrollThreshold.current);
        searchFormContainerElRef.current?.classList.add('sticky-search-container');
        searchFormContainerElRef.current?.classList.remove('sticky-search-top');
        searchFormContainerElRef.current?.classList.remove('search-container-top');
      } else {
        /**
         * When scrolling up remove the sticky search container class
         */
        setIsStickySearchForm(true);
        handleScrollPosition(updatedCurrentScrollPosition, scrollThreshold.current);
        searchFormContainerElRef.current?.classList.add('sticky-search-top');
        setTopPosition(homePageHeaderHeight);
        searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      }
    } else if (
      updatedCurrentScrollPosition > lastScrollPosition.current ||
      (updatedCurrentScrollPosition < lastScrollPosition.current && updatedCurrentScrollPosition < homePageHeaderHeight)
    ) {
      // When scroll position is less than or equal to the top offset of the searchform
      handleScrollPosition(updatedCurrentScrollPosition, scrollThreshold.current);
      /**
       * When scrolling up or scrolling down, remove the sticky search container class
       */
      setIsStickySearchForm(false);
      searchFormContainerElRef.current?.classList.remove('sticky-search-top');
      searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      searchFormContainerElRef.current?.classList.remove('search-container-top');
    }
    lastScrollPosition.current = currentScrollPosition;
  };

  useEffect(() => {
    if (isPositionedExternally && !isExternal) {
      return;
    }

    /**
     * adding the scroll event if handleScroll triggers
     * removes scroll if were back to original state
     */
    if (isPositionedExternally && isExternal) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [TabbedForm, searchformScrollThreshold]);

  const [formLoaded, setFormLoaded] = useState(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAvailabilityScenario = props?.hideDestination && props?.hideBrandsField;
    // Retrieve the URL parameters from the window location search string
    const fromDateParam = urlParams?.get('fromDate') || urlParams?.get('fromDates');
    const fromDates = fromDateParam ? getDateFormattedFromURL(fromDateParam) : null;
    const toDateParam = urlParams?.get('toDate') || urlParams?.get('toDates');
    const toDates = toDateParam ? getDateFormattedFromURL(toDateParam) : null;
    const searchCriteria = globalData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria;
    // Attempt to retrieve the property code from either The global session data object if available or URL parameters if the global data is not available.
    const propertyCode = urlParams?.get('propertyCode') || searchCriteria?.propertyId;
    const fromDate = fromDates;
    const toDate = toDates;
    const numofRooms = urlParams?.get('numberOfRooms') || searchCriteria?.availabilityRequestVO?.numRooms;
    const numofAdults = urlParams?.get('numberOfGuests') || searchCriteria?.availabilityRequestVO?.numAdultsPerRoom;
    const numofChildren = urlParams?.get('childrenCount') || searchCriteria?.availabilityRequestVO?.numChildrenPerRoom;
    // Check if both 'hideDestination' and 'hideBrandsField' props are true, and if a property code exists.
    if (isAvailabilityScenario && propertyCode) {
      setSearchFormState(
        [DESTINATION],
        {
          [DESTINATION]: {
            displayText: propertyCode,
            destinationAddressPlaceId: propertyCode,
          },
        },
        true
      );
      // Update the search form state to include additional details for the destination field.
      setSearchFormState(['destinationFieldDetails'], {
        destinationFieldDetails: {
          recentlyViewedPopertyCode: propertyCode,
          isRecentSearchSelected: true,
        },
      });
    }
    if (isAvailabilityScenario && fromDate && toDate) {
      const lengthOfStay = searchCriteria?.availabilityRequestVO?.lengthOfStay;
      setSearchFormState([DATES], {
        [DATES]: {
          fromDate: getDateObj(fromDate),
          toDate: getDateObj(toDate),
          flexible: searchCriteria?.availabilityRequestVO?.flexibleDate,
          numberOfNights:
            fromDate && toDate
              ? Number(getTotalNumberOfDays(moment(fromDate), moment(toDate))?.toFixed(0) || 1)
              : lengthOfStay,
          lengthOfStay: fromDate && toDate ? getTotalNumberOfDays(moment(fromDate), moment(toDate)) : lengthOfStay,
        },
      });
    }
    if (isAvailabilityScenario && numofRooms) {
      setSearchFormState([ROOMS_AND_GUESTS], {
        [ROOMS_AND_GUESTS]: {
          numRooms: getRoomRange(numofRooms),
          ...(numofAdults && { numAdultsPerRoom: parseInt(numofAdults, 10) }),
          ...(numofChildren && { numChildrenPerRoom: parseInt(numofChildren, 10) }),
        },
      });
    } else return;
  }, [globalData?.sessionData]);

  const getSearchformComponent = (TabbedForm: boolean) => {
    if (TabbedForm) {
      return (
        <div ref={isBaseLineSearchTabbedView ? searchFormPositionWrapperRef : undefined}>
          <StyledSearchForm
            data-component-name="o-luxury-searchform"
            data-testid="searchform"
            ref={searchFormContainerElRef}
            disableDropShadow={disableDropShadow}
            className={clsx(
              'd-flex',
              props?.model?.['styleclass'],
              model?.isTabbedSearchForm !== true && 'px-3',
              'document_search_form_container_el_ref'
            )}
          >
            <StaticDataContextProvider value={{ ...model, currentLocale, brandSlug }}>
              <div
                ref={searchFormContainerRef}
                className={clsx(
                  'container px-0',
                  model?.isTabbedSearchForm ? '' : 'search-form-wrapper',
                  'document_search_form_container',
                  'color-scheme1'
                )}
              >
                {!formLoaded ? (
                  <></>
                ) : (
                  <SearchFormView
                    {...{
                      sessionData: globalData,
                      model,
                      currentLocale,
                      hideBrandsField: props.hideBrandsField,
                      hideDestination: props.hideDestination,
                      isNonStickyVariant: isNonStickyVariant,
                      destinationColStyleOverride: props.destinationColStyleOverride,
                      isStickySearchForm: isStickySearchForm,
                      isSticky: props.isSticky,
                      disableDropShadow: disableDropShadow,
                    }}
                  ></SearchFormView>
                )}
              </div>
            </StaticDataContextProvider>
          </StyledSearchForm>
        </div>
      );
    } else {
      return (
        <StyledSearchFormWrapper
          data-component-name="o-luxury-searchform"
          data-testid="searchform"
          data-sticky-fixed-bottom={isSticky ? true : undefined}
          ref={searchFormPositionWrapperRef}
          className={clsx(
            !isSearchFormUsedInBrandsPage && !isSticky && 'pt-5',
            props?.model?.['styleclass'] ? props?.model?.['styleclass'] : 'm-container-fullbleed',
            {
              'search-form-position': searchFormOnHomepage,
            }
          )}
          topOffset={toRem(topPosition)}
          isNonStickyVariant={isNonStickyVariant}
          $isSticky={isSticky}
        >
          <StyledSearchForm
            ref={searchFormContainerElRef}
            disableDropShadow={disableDropShadow}
            className={clsx(
              'd-flex',
              model?.isTabbedSearchForm !== true && 'px-3',
              'document_search_form_container_el_ref',
              'py-5',
              {
                'search-wrapped': isPositionedExternally,
                'sticky-search-wrapped color-scheme1': isPositionedExternally && isSticky,
              }
            )}
            style={{ top: toRem(topPosition) }}
          >
            <StaticDataContextProvider value={{ ...model, currentLocale, brandSlug }}>
              <div
                ref={searchFormContainerRef}
                className={clsx(
                  'container px-0',
                  model?.isTabbedSearchForm ? '' : 'search-form-wrapper',
                  'document_search_form_container',
                  { 'color-scheme1': (!isPositionedExternally || !isSticky) && !disableDropShadow }
                )}
              >
                <SearchFormView
                  {...{
                    sessionData: globalData,
                    model,
                    currentLocale,
                    hideBrandsField: props.hideBrandsField,
                    hideDestination: props.hideDestination,
                    isNonStickyVariant: isNonStickyVariant,
                    destinationColStyleOverride: props.destinationColStyleOverride,
                    isStickySearchForm: isStickySearchForm,
                    isSticky: props.isSticky,
                    disableDropShadow: disableDropShadow,
                  }}
                ></SearchFormView>
              </div>
            </StaticDataContextProvider>
          </StyledSearchForm>
        </StyledSearchFormWrapper>
      );
    }
  };

  /**
   * TODO: All store calculation will be done as part of this file
   * We will pass proccess data to useSEarchForm in the form of props
   */
  return <>{getSearchformComponent(TabbedForm)}</>;
});

//Config for aem editable components
export const SearchFormConfig = {
  emptyLabel: 'SearchForm',
  isEmpty: false,
  resourceType: `mi-aem-luxury-spa/components/content/baselinesearchform/v1/baselinesearchform`,
};
//Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchFormEditable = (props: any) => {
  // Authorable via AEM dialog as `./lengthOfStayDefaultOverride` and exported on model.json.
  // Only pass through when it's a valid positive number; otherwise omit so we don't force a global default.
  const los = props?.model?.lengthOfStayDefaultOverride;
  const lengthOfStayDefaultOverride = typeof los === 'number' && Number.isFinite(los) && los > 0 ? los : undefined;

  return props?.cqPath?.includes('experiencefragment') ? (
    <SearchForm {...props} lengthOfStayDefaultOverride={lengthOfStayDefaultOverride} />
  ) : (
    <EditableComponent config={SearchFormConfig} {...props}>
      <SearchForm {...props} lengthOfStayDefaultOverride={lengthOfStayDefaultOverride} />
    </EditableComponent>
  );
};
